import { ref, computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { createSharedComposable } from '@vueuse/core'
import { useAddressStore } from '@/pinia/address'
import { getPriceByQuantity } from '@/utils/utils'
import { usePlugin } from '@/hooks/usePlugin'
import { calcShippingFee, removeCart, retrieveCartList } from '@/api/cart'
import { useCartStore } from '@/pinia/cart'
import { useShipStore } from '@/pinia/ship'
import { useCurrencyStore } from '@/pinia/currency'

export const useCart = createSharedComposable(() => {
  const initializing = ref(false)

  const calculating = ref(false)

  const loading = ref(false)

  const frontCartStats = ref(null)

  const { $toast, $confirm } = usePlugin()

  const cartStore = useCartStore()
  const ship = useShipStore()
  const addressStore = useAddressStore()

  const currencyStore = useCurrencyStore()

  const cartList = ref([])

  const channelCode = ref(2)

  const currencyData = computed(() => currencyStore.current)

  const getCartList = async () => {
    const params = {
      page: 1,
      pageSize: 200
    }

    if (frontCartStats.value != null) params.frontCartStats = frontCartStats.value

    const { code, data } = await retrieveCartList(params, {
      failedRedirect: true
    })
    if (code === 200) {
      const list = data.rows || []

      cartList.value = list.map(item => {
        const skuDetails = item.skuDetails.map(sku => ({
          ...sku,
          checked: !(isUnavailable(sku.cartStatus) || isProcessing(sku.cartStatus)),
          disabled: isUnavailable(sku.cartStatus),
          uniqueKey: uuidv4(),
          spuId: item.offerId,
          categoryId: item.categoryId,
          undeliverable: false,
          batchNum: item.batchNum,
          price: 0
        }))

        updateSkuPrice(skuDetails)

        const disabled = skuDetails.every(item => item.disabled)

        return {
          ...item,
          skuDetails,
          checked: disabled ? false : skuDetails.every(item => item.disabled || item.checked),
          disabled,
          /**
           * 存储该 spu 是否显示可减免提示
           */
          bulkPackFlag: false,
          /**
           * 判断是否是真实物流价, 用于判断是否显示可减免提示
           * 默认为 true，这样只在 物流价接口 有返回时才会验证是否展示
           */
          hasConfirmedPackage: true,
          shipAmount: null
        }
      })
    }
  }

  /**
   * 防止请求竞态导致列表数据展示不正确问题
   */
  let latestRequestId = 0

  const calcShipFee = async (skuDetails) => {
    const currentRequestId = ++latestRequestId
    calculating.value = true
    const params = {
      address: addressStore.cacheAddress,
      currency: currencyData.value.targetCurrency,
      userCurrency: currencyData.value.targetCurrency,
      fillAllUnShip: false,
      details: skuDetails.map(item => {
        return {
          spuId: item.spuId,
          skuId: item.skuId,
          skuType: item.skuType,
          skuQuantity: item.quantity,
          categoryId: item.categoryId,
          uniqueKey: item.uniqueKey,
          spuName: item.spuName,
          skuImage: item.skuImageUrl,
          skuSpec: item.skuSpec,
          skuPriceCny: item.price,
          skuFeature: item.skuFeature
        }
      })
    }
    if (params.address == null) {
      params.address = {
        country: ship.country.countryName,
        countryCode: ship.country.countryCode
      }
    }

    const { code, data: { details } } = await calcShippingFee(params)
    if (code === 200 && currentRequestId === latestRequestId) {
      const { channelCode: channel = 2, spuShipPackages = [], shipPackage = [] } = details[0] ?? {}

      channelCode.value = channel

      spuShipPackages.forEach(item => {
        cartList.value.forEach(cart => {
          if (cart.offerId === item.spuId) {
            const shipPackage = item.shipPackage
            if (shipPackage.length > 0) {
              let shipAmount = 0
              shipPackage.forEach(item => {
                shipAmount += Number(item.shipAmount)
              })
              cart.shipAmount = shipAmount.toFixed(2)
            }
            cart.bulkPackFlag = item.bulkPackFlag
            cart.hasConfirmedPackage = shipPackage.some(item => item.confirmed)
          }
        })
      })

      /**
       * 存储有返回结果的 skuId 列表，用于无返回结果的 sku 列表添加 unavailableMsg
       */
      const shippedUniqueKeys = []

      shipPackage.forEach(item => {
        shippedUniqueKeys.push(...item.uniqueKeys)
      })

      const unshippedSpu = new Set()

      skuDetails.forEach(item => {
        if (!shippedUniqueKeys.includes(item.uniqueKey)) {
          item.undeliverable = true

          unshippedSpu.add(item.spuId)
        } else {
          /**
           * 商品如果正常返回物流结果，则意味着可以下单，这时候重置 undeliverable 状态即可
           */
          item.undeliverable = false
        }
      })

      /**
       * 商品无法送达需要将该 spu hasConfirmedPackage 设置为 false
       */
      cartList.value.forEach(cart => {
        if (unshippedSpu.has(cart.offerId)) {
          cart.hasConfirmedPackage = false
        }
      })

      calculating.value = false

      if (shipPackage.length === 0) return null

      return shipPackage.reduce((pre, cur) => {
        pre.minDeliveryTime = Math.min(pre.minDeliveryTime, cur.minDeliveryTime)
        pre.maxDeliveryTime = Math.max(pre.maxDeliveryTime, cur.maxDeliveryTime)
        pre.shipAmount = (Number(pre.shipAmount) + Number(cur.shipAmount)).toFixed(2)
        pre.shipAmountUsd = (Number(pre.shipAmountUsd) + Number(cur.shipAmountUsd)).toFixed(2)
        return pre
      })
    }
  }

  const isProcessing = (status) => {
    return status === 0
  }

  const isUnavailable = (status) => {
    return [50, 51, 52].includes(Number(status))
  }

  const updateSkuPrice = (skuDetails) => {
    if (typeof skuDetails === 'string') {
      skuDetails = cartList.value.find(item => item.offerId === skuDetails).skuDetails
    }

    const checkedSku = skuDetails.filter(item => item.checked)

    const checkedQuantity = checkedSku.reduce((pre, cur) => {
      if (cur.skuType === 2) return pre
      return pre + cur.quantity
    }, 0)

    checkedSku.forEach(item => {
      item.price = getPriceByQuantity(checkedQuantity, item.priceRangeList)
    })

    const uncheckedSku = skuDetails.filter(item => !item.checked)

    const uncheckedQuantity = uncheckedSku.reduce((pre, cur) => {
      if (cur.skuType === 2) return pre
      return pre + cur.quantity
    }, 0)

    uncheckedSku.forEach(item => {
      item.price = getPriceByQuantity(uncheckedQuantity, item.priceRangeList)
    })
  }

  const isCheckedAll = computed(() => cartList.value.every(item => item.disabled || item.checked))

  const handleCheckAll = () => {
    cartList.value.forEach(item => {
      item.skuDetails.forEach(sku => {
        if (!isUnavailable(sku.cartStatus)) {
          sku.checked = !isCheckedAll.value
        }
      })
    })
  }

  const handleCheckedDelete = () => {
    $confirm('Delete item?', 'Are you sure you want to delete item from cart?').then(async () => {
      const cartIds = []

      cartList.value.forEach(spu => {
        spu.skuDetails.forEach(v => {
          if (v.checked) {
            cartIds.push(v.cartId)
          }
        })
      })

      const { code } = await removeCart({
        cartIds
      })
      if (code === 200) {
        $toast('Deleted successfully.', { type: 'success' })

        cartStore.updateCartCount()

        for (let i = 0; i < cartList.value.length; i++) {
          const spu = cartList.value[i]
          for (let j = 0; j < spu.skuDetails.length; j++) {
            const sku = spu.skuDetails[j]
            if (cartIds.includes(sku.cartId)) {
              spu.skuDetails.splice(j, 1)
              j--
            }
          }
          if (spu.skuDetails.length === 0) {
            cartList.value.splice(i, 1)
            i--
          }
        }
      }
    })
  }

  const onInit = async () => {
    loading.value = true

    await getCartList()
    loading.value = false
  }

  watch(frontCartStats, () => {
    onInit()
  })

  onBeforeMount(async () => {
    initializing.value = true
    await onInit()
    initializing.value = false
  })

  return {
    initializing,
    loading,
    calculating,
    frontCartStats,
    cartList,
    isAllEmpty: computed(() => frontCartStats.value == null && cartList.value.length === 0 && !loading.value),
    isCheckedAll,
    channelCode,
    getCartList,
    calcShipFee,
    isUnavailable,
    isProcessing,
    updateSkuPrice,
    handleCheckAll,
    handleCheckedDelete
  }
})
